/**
 * Data layer for `dpf`. Thin typed fetch wrappers over /api/v1/dpf.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface DpfRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListDpfResponse {
	count: number;
	items: DpfRow[];
}

export interface CreateDpfBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/dpf";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListDpfResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListDpfResponse>(res);
}

export async function create(body: CreateDpfBody): Promise<DpfRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<DpfRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const dpfQueryKeys = {
	all: ["dpf"] as const,
	list: () => [...dpfQueryKeys.all] as const,
	detail: (id: string) => [...dpfQueryKeys.all, id] as const,
};
